import React, { useState, useEffect, useRef } from 'react';
import { FileText, ChevronDown, Plus, Settings, X, Trash2, Loader2 } from 'lucide-react';
import TemplateCreateModal from './TemplateCreateModal';

// Delete Confirmation Dialog Component
const DeleteConfirmDialog = ({ isOpen, onClose, onConfirm, templateName, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          disabled={isDeleting}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold mb-4">Delete Template</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{templateName}"? This action cannot be undone.
        </p>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium flex items-center gap-2 disabled:opacity-50"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplateSelector = ({
  selectedTemplate = null,
  onTemplateChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showManageView, setShowManageView] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowManageView(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/user/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (template) => {
    if (onTemplateChange && typeof onTemplateChange === 'function') {
      onTemplateChange(template);
    }
    setIsOpen(false);
    setShowManageView(false);
  };

  const handleClearTemplate = () => {
    if (onTemplateChange && typeof onTemplateChange === 'function') {
      onTemplateChange(null);
    }
    setIsOpen(false);
    setShowManageView(false);
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowCreateModal(true);
    setIsOpen(false);
    setShowManageView(false);
  };

  const handleEditTemplate = (template, e) => {
    e.stopPropagation();
    setEditingTemplate(template);
    setShowCreateModal(true);
    setIsOpen(false);
    setShowManageView(false);
  };

  const handleDeleteClick = (template, e) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/user/templates/${templateToDelete._id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchTemplates();
        // Clear selection if deleted template was selected
        if (selectedTemplate && selectedTemplate._id === templateToDelete._id) {
          handleClearTemplate();
        }
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  const getDisplayName = () => {
    if (selectedTemplate) {
      return selectedTemplate.name;
    }
    return '+ Template';
  };

  return (
    <div ref={dropdownRef} className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <div className="flex items-center gap-3">
        <FileText className="w-5 h-5 text-gray-500" />

        {/* Template selector button */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`relative w-48 flex items-center justify-between px-3 py-2 text-left bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          } ${selectedTemplate ? 'text-teal-600 border-teal-300' : ''}`}
          disabled={disabled}
        >
          <span className="block truncate">
            {getDisplayName()}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Clear button when template is selected */}
        {selectedTemplate && !disabled && (
          <button
            type="button"
            onClick={handleClearTemplate}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full focus:outline-none"
            title="Clear template"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-20 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 left-8">
          {showManageView ? (
            // Manage templates view
            <div className="py-2">
              <div className="px-3 py-2 border-b border-gray-100 flex justify-between items-center">
                <span className="font-medium text-gray-700">Manage Templates</span>
                <button
                  onClick={() => setShowManageView(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {isLoading ? (
                <div className="px-3 py-4 text-center text-gray-500">Loading...</div>
              ) : templates.length === 0 ? (
                <div className="px-3 py-4 text-center text-gray-500">No templates yet</div>
              ) : (
                <ul className="max-h-60 overflow-auto py-1">
                  {templates.map((template) => (
                    <li
                      key={template._id}
                      className="px-3 py-2 hover:bg-gray-50 flex justify-between items-center"
                    >
                      <span className="truncate flex-1">{template.name}</span>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={(e) => handleEditTemplate(template, e)}
                          className="p-1 text-gray-400 hover:text-teal-600 rounded"
                          title="Edit template"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(template, e)}
                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                          title="Delete template"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            // Normal selection view
            <div className="py-1">
              {/* Create new option */}
              <button
                onClick={handleCreateNew}
                className="w-full px-3 py-2 text-left hover:bg-teal-50 text-teal-600 flex items-center gap-2 border-b border-gray-100"
              >
                <Plus className="w-4 h-4" />
                <span>Create New Template</span>
              </button>

              {/* No template option */}
              <button
                onClick={handleClearTemplate}
                className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${
                  !selectedTemplate ? 'text-teal-600 bg-teal-50' : 'text-gray-700'
                }`}
              >
                No Template
              </button>

              {/* Templates list */}
              {isLoading ? (
                <div className="px-3 py-4 text-center text-gray-500">Loading...</div>
              ) : templates.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-1"></div>
                  <ul className="max-h-48 overflow-auto">
                    {templates.map((template) => (
                      <li
                        key={template._id}
                        onClick={() => handleSelect(template)}
                        className={`cursor-pointer select-none px-3 py-2 hover:bg-teal-50 ${
                          selectedTemplate && selectedTemplate._id === template._id
                            ? 'text-teal-600 bg-teal-50'
                            : 'text-gray-900'
                        }`}
                      >
                        <span className="block truncate">{template.name}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Manage templates option */}
              {templates.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => setShowManageView(true)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-600 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Manage Templates</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TemplateCreateModal
          isOpen={showCreateModal}
          onClose={handleModalClose}
          template={editingTemplate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        templateName={templateToDelete?.name || ''}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default TemplateSelector;
